import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import AdminSidebar from '../components/AdminSidebar';
import {
  Search,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  Filter,
  Calendar,
  Upload,
  Loader2,
  ChevronDown,
  ChevronUp,
  Ban,
  Trash2,
  Download
} from 'lucide-react';
import { downloadUpload } from '../lib/download';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const isToday = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  return date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();
};

const isThisMonth = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  return date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth();
};

const getActionColor = (action) => {
  if (action.includes('login')) return 'text-blue-400 bg-blue-400/10';
  if (action.includes('upload')) return 'text-green-400 bg-green-400/10';
  if (action.includes('delete')) return 'text-red-400 bg-red-400/10';
  if (action.includes('create')) return 'text-yellow-400 bg-yellow-400/10';
  if (action.includes('update')) return 'text-purple-400 bg-purple-400/10';
  if (action.includes('branding')) return 'text-pink-400 bg-pink-400/10';
  return 'text-zinc-400 bg-zinc-400/10';
};

const AdminLogs = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [limit, setLimit] = useState(100);
  const [activeUploads, setActiveUploads] = useState([]);
  const [activeUploadsOpen, setActiveUploadsOpen] = useState(true);
  const [rejectMenuOpen, setRejectMenuOpen] = useState(null);
  const [completedUploads, setCompletedUploads] = useState([]);
  const [completedUploadsOpen, setCompletedUploadsOpen] = useState(true);

  const REJECTION_REASONS = [
    "Show Not Titled Correctly. Please use this format: Sunday March 11th at 3.30 PM - Pastor John Doe",
    "Show Is not the correct length. Show must be either 28:30 or 58:30 for hour long broadcasts"
  ];

  const handleRejectActive = async (sessionId, reason) => {
    try {
      await axios.post(`${API}/admin/active-uploads/${sessionId}/reject`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Upload rejected');
      setRejectMenuOpen(null);
      fetchActiveUploads();
    } catch (err) {
      toast.error('Failed to reject upload');
    }
  };

  const handleRejectCompleted = async (sessionOrUploadId, reason) => {
    try {
      // Try rejecting as a completed upload record first
      let res;
      try {
        res = await axios.post(`${API}/admin/uploads/${sessionOrUploadId}/reject`,
          { reason },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (e) {
        // If not found as upload record, try as active upload session
        res = await axios.post(`${API}/admin/active-uploads/${sessionOrUploadId}/reject`,
          { reason },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      toast.success('Upload rejected and file deleted');
      setRejectMenuOpen(null);
      fetchCompletedUploads();
      fetchActiveUploads();
      fetchLogs();
    } catch (err) {
      toast.error('Failed to reject upload');
    }
  };

  const handleClearStale = async () => {
    try {
      const res = await axios.delete(`${API}/admin/active-uploads/clear-stale`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchActiveUploads();
    } catch (err) {
      toast.error('Failed to clear stale uploads');
    }
  };

  const handleReprocessStuck = async () => {
    try {
      const res = await axios.post(`${API}/admin/uploads/reprocess-stuck`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Re-processing ${res.data.reprocessed} stuck uploads. ${res.data.skipped_missing} files missing.`);
      fetchActiveUploads();
    } catch (err) {
      toast.error('Failed to re-process stuck uploads');
    }
  };

  const handleDownload = (uploadId, filename) => downloadUpload(uploadId, filename, token);


  const fetchLogs = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      if (actionFilter !== 'all') {
        params.append('action', actionFilter);
      }
      
      const response = await axios.get(`${API}/admin/logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to load logs');
    } finally {
      setIsLoading(false);
    }
  }, [token, actionFilter, limit]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Fetch active uploads and auto-refresh
  const fetchActiveUploads = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/active-uploads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveUploads(res.data);
    } catch (err) {
      console.error('Failed to fetch active uploads:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchActiveUploads();
    const interval = setInterval(fetchActiveUploads, 3000);
    return () => clearInterval(interval);
  }, [fetchActiveUploads]);

  const fetchCompletedUploads = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/uploads?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompletedUploads(res.data.filter(u => u.processing_status !== 'rejected'));
    } catch (err) {
      console.error('Failed to fetch completed uploads:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchCompletedUploads();
  }, [fetchCompletedUploads]);

  const filteredLogs = logs.filter(log => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = (
      log.action.toLowerCase().includes(searchLower) ||
      (log.username && log.username.toLowerCase().includes(searchLower)) ||
      log.details.toLowerCase().includes(searchLower)
    );
    
    let matchesTime = true;
    if (timeFilter === 'today') {
      matchesTime = isToday(log.timestamp);
    } else if (timeFilter === 'month') {
      matchesTime = isThisMonth(log.timestamp);
    }
    
    return matchesSearch && matchesTime;
  });

  // Stats for upload activity
  const uploadLogs = filteredLogs.filter(l => l.action === 'upload_complete' && l.success);
  const uploaderStats = uploadLogs.reduce((acc, log) => {
    const name = log.username || 'Unknown';
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});

  const actionTypes = [
    { value: 'all', label: 'All Actions' },
    { value: 'login', label: 'Logins' },
    { value: 'upload_complete', label: 'Uploads Complete' },
    { value: 'upload_init', label: 'Uploads Started' },
    { value: 'video_converted', label: 'Converted' },
    { value: 'video_conversion_failed', label: 'Conversion Failed' },
    { value: 'conversion_queue', label: 'Conversion Queue' },
    { value: 'create_user', label: 'User Created' },
    { value: 'update_user', label: 'User Updated' },
    { value: 'delete_user', label: 'User Deleted' },
    { value: 'update_branding', label: 'Branding' }
  ];

  return (
    <div className="min-h-screen bg-[#09090B] flex" data-testid="admin-logs">
      <AdminSidebar />
      
      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-display text-4xl text-white tracking-tight">ACTIVITY LOGS</h1>
              <p className="text-zinc-500 mt-1">View all system activity and audit trail</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchLogs}
              className="border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800"
              data-testid="refresh-logs-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                type="text"
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-zinc-900 border-zinc-800 text-white h-10"
                data-testid="search-logs-input"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-zinc-500" />
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="w-[160px] bg-zinc-900 border-zinc-800 text-white">
                  <SelectValue placeholder="Filter by action" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {actionTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-zinc-500" />
              <Select value={timeFilter} onValueChange={setTimeFilter}>
                <SelectTrigger className="w-[140px] bg-zinc-900 border-zinc-800 text-white" data-testid="time-filter">
                  <SelectValue placeholder="Time period" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Select value={limit.toString()} onValueChange={(v) => setLimit(parseInt(v))}>
              <SelectTrigger className="w-[100px] bg-zinc-900 border-zinc-800 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="250">250</SelectItem>
                <SelectItem value="500">500</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Active Uploads - Collapsible with REJECT buttons */}
          {activeUploads.length > 0 && (
            <div className="mb-6 bg-zinc-900/50 border border-green-800/50 rounded-xl overflow-hidden" data-testid="active-uploads">
              <button
                onClick={() => setActiveUploadsOpen(!activeUploadsOpen)}
                className="w-full flex items-center justify-between p-4 hover:bg-zinc-800/30 transition-colors"
                data-testid="toggle-active-uploads"
              >
                <div className="flex items-center gap-2">
                  <Upload className="w-4 h-4 text-green-400" />
                  <h3 className="text-sm font-medium text-green-400 uppercase tracking-wider">
                    Active Uploads ({activeUploads.length})
                  </h3>
                  <Loader2 className="w-3 h-3 text-green-400 animate-spin" />
                </div>
                <div className="flex items-center gap-2">
                  <span
                    onClick={(e) => { e.stopPropagation(); handleReprocessStuck(); }}
                    className="text-xs text-zinc-500 hover:text-yellow-400 cursor-pointer px-2 py-1 hover:bg-zinc-800 rounded"
                    data-testid="reprocess-stuck-btn"
                  >
                    <RefreshCw className="w-3 h-3 inline mr-1" />
                    Re-process Stuck
                  </span>
                  <span
                    onClick={(e) => { e.stopPropagation(); handleClearStale(); }}
                    className="text-xs text-zinc-500 hover:text-red-400 cursor-pointer px-2 py-1 hover:bg-zinc-800 rounded"
                    data-testid="clear-stale-btn"
                  >
                    <Trash2 className="w-3 h-3 inline mr-1" />
                    Clear All
                  </span>
                  {activeUploadsOpen ? (
                    <ChevronUp className="w-4 h-4 text-zinc-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-zinc-400" />
                  )}
                </div>
              </button>
              {activeUploadsOpen && (
                <div className="px-4 pb-4 space-y-3">
                  {/* Conversion Status Summary */}
                  {(activeUploads.some(u => u.processing_status === 'converting' || u.processing_status === 'conversion_queue')) && (
                    <div className="flex items-center gap-4 p-3 bg-zinc-800/80 border border-zinc-700 rounded-lg" data-testid="conversion-summary">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse"></span>
                        <span className="text-xs text-yellow-400 font-medium">
                          Converting: {activeUploads.filter(u => u.processing_status === 'converting').length}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-orange-400"></span>
                        <span className="text-xs text-orange-400 font-medium">
                          In Queue: {activeUploads.filter(u => u.processing_status === 'conversion_queue').length}
                        </span>
                      </div>
                    </div>
                  )}
                  {activeUploads.map(upload => (
                    <div key={upload.id} className="p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-white">{upload.username}</span>
                          {upload.processing_status === 'converting' ? (
                            <span className="px-1.5 py-0.5 text-xs uppercase bg-yellow-500/20 text-yellow-400 rounded font-medium animate-pulse">Converting</span>
                          ) : upload.processing_status === 'conversion_queue' ? (
                            <span className="px-1.5 py-0.5 text-xs uppercase bg-orange-500/20 text-orange-400 rounded font-medium">In Queue</span>
                          ) : upload.processing_status === 'validating' ? (
                            <span className="px-1.5 py-0.5 text-xs uppercase bg-purple-500/20 text-purple-400 rounded font-medium">Checking</span>
                          ) : upload.processing_status === 'complete' ? (
                            <span className="px-1.5 py-0.5 text-xs uppercase bg-green-500/20 text-green-400 rounded font-medium">Ready</span>
                          ) : upload.session_status === 'completed' ? (
                            <span className="px-1.5 py-0.5 text-xs uppercase bg-blue-500/20 text-blue-400 rounded font-medium">Uploaded</span>
                          ) : (
                            <span className="text-xs text-zinc-500">uploading</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-mono text-green-400">{upload.progress}%</span>
                          {upload.processing_status === 'complete' && upload.upload_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownload(upload.upload_id, upload.filename)}
                              className="h-7 px-2 text-zinc-500 hover:text-green-400 hover:bg-green-500/10"
                              data-testid={`download-active-btn-${upload.id}`}
                            >
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <div className="relative">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setRejectMenuOpen(rejectMenuOpen === upload.id ? null : upload.id)}
                              className="h-7 px-2 bg-red-600/20 text-red-400 hover:bg-red-600/40 hover:text-red-300 border border-red-800/50"
                              data-testid={`reject-active-btn-${upload.id}`}
                            >
                              <Ban className="w-3.5 h-3.5 mr-1" />
                              <span className="text-xs font-bold">REJECT</span>
                            </Button>
                            {rejectMenuOpen === upload.id && (
                              <div className="absolute right-0 top-full mt-1 w-96 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-50 overflow-y-auto max-h-80">
                                <p className="px-3 py-2 text-xs text-zinc-400 uppercase tracking-wider border-b border-zinc-800 font-bold">Select Rejection Reason</p>
                                {REJECTION_REASONS.map((reason, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() => {
                                      if (upload.session_status === 'completed') {
                                        handleRejectCompleted(upload.id, reason);
                                      } else {
                                        handleRejectActive(upload.id, reason);
                                      }
                                    }}
                                    className="w-full text-left px-3 py-3 text-sm text-zinc-300 hover:bg-red-500/10 hover:text-red-400 transition-colors border-b border-zinc-800 last:border-0"
                                    data-testid={`reject-reason-${idx}`}
                                  >
                                    {reason}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-zinc-400 truncate mb-2" title={upload.processing_message || ''}>{upload.filename}</p>
                      <div className="relative h-2 bg-zinc-700 rounded-full overflow-hidden">
                        <div 
                          className={`absolute top-0 left-0 h-full rounded-full transition-all duration-500 ${
                            upload.session_status === 'completed' ? 'bg-blue-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${upload.progress}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-1 text-xs text-zinc-500">
                        <span>{upload.received_chunks}/{upload.total_chunks} chunks</span>
                        <span>{(upload.file_size / (1024 * 1024 * 1024)).toFixed(2)} GB</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Upload Summary */}
          {Object.keys(uploaderStats).length > 0 && (
            <div className="mb-6 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl" data-testid="upload-summary">
              <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-3">
                {timeFilter === 'today' ? "Today's" : timeFilter === 'month' ? "This Month's" : 'All'} Uploads by User
              </h3>
              <div className="flex flex-wrap gap-3">
                {Object.entries(uploaderStats)
                  .sort((a, b) => b[1] - a[1])
                  .map(([username, count]) => (
                    <div key={username} className="flex items-center gap-2 px-3 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg">
                      <span className="text-white text-sm font-medium">{username}</span>
                      <span className="px-2 py-0.5 text-xs font-bold bg-green-500/20 text-green-400 rounded-full">{count}</span>
                    </div>
                  ))
                }
              </div>
              <p className="text-xs text-zinc-500 mt-2">
                {uploadLogs.length} total upload{uploadLogs.length !== 1 ? 's' : ''} {timeFilter === 'today' ? 'today' : timeFilter === 'month' ? 'this month' : ''}
              </p>
            </div>
          )}

          {/* Logs Table */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
            <ScrollArea className="h-[600px]">
              <Table>
                <TableHeader className="sticky top-0 bg-zinc-900 z-10">
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-400 uppercase text-xs tracking-wider w-12">Status</TableHead>
                    <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Timestamp</TableHead>
                    <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Action</TableHead>
                    <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">User</TableHead>
                    <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Details</TableHead>
                    <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">IP Address</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12">
                        <div className="w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : filteredLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12 text-zinc-500">
                        No logs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLogs.map(log => (
                      <TableRow 
                        key={log.id} 
                        className="border-zinc-800 hover:bg-zinc-800/50"
                        data-testid={`log-row-${log.id}`}
                      >
                        <TableCell>
                          {log.success ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-500" />
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-zinc-400 text-xs font-mono">
                            <Clock className="w-3 h-3" />
                            {formatDate(log.timestamp)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 text-xs uppercase font-medium ${getActionColor(log.action)}`}>
                            {log.action.replace(/_/g, ' ')}
                          </span>
                        </TableCell>
                        <TableCell>
                          {log.username ? (
                            <span className="text-white text-sm">{log.username}</span>
                          ) : (
                            <span className="text-zinc-600 text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-zinc-400 text-sm truncate max-w-[300px] block" title={log.details}>
                            {log.details}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-zinc-500 text-xs font-mono">
                            {log.ip_address || '—'}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>

          {/* Summary */}
          <div className="mt-4 flex items-center justify-between text-sm text-zinc-500">
            <span>Showing {filteredLogs.length} of {logs.length} logs</span>
            <span>
              {logs.filter(l => l.success).length} successful, {logs.filter(l => !l.success).length} failed
            </span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminLogs;
